import React from 'react';
import { makeStyles, Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FormText from '../components/FormText/FormText';
import useBuildSummaryForm from '../hooks/buildSummaryStep/useBuildSummaryForm';
import allActions from '../store/actions';
import ButtonNavigation from '../components/ButtonNavigation/ButtonNavigation';
import { getTranslation } from '../utils/functions';
import buildPATHS, { getPathWithBaseUrl } from '../utils/paths';
import { mapDataBeforeSave } from '../utils/FormDataMappers';
import { RootStore } from '../store/reducers';
import { TEXT_TYPES } from '../constants/formElements';
import { CustomTheme } from '../appTemplates/MUIProviderTemplate/MUIProviderTemplateInterfaces';
import {
  summaryPageFieldsFromAccountSelector,
  summaryTextSelector,
  textsOverwritingsSelector,
} from '../store/selectors/globalConfigSelectors';
import {
  customFieldsSelector,
  registerDataSelector,
} from '../store/selectors/formConfigSelectors';
import {
  formInitialValuesSelector,
  languageSelector,
  previewFormSelector,
} from '../store/selectors/appSelectors';
import { PATHS_WITH_PARAMS } from '../utils/paths';
import AccountDetails from './accounts/details/AccountDetails';
import { isAuthenticated } from '../utils/authService';
import { magicFieldsDataSelector } from '../store/selectors/formSessionSelectors';
import Typography from '@hid-galaxy-ui/galaxy-react/components/Typography';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import { replaceMagicFields } from '../components/ApplicationSentContent/helpers';

const useTitleStyles = makeStyles((theme: CustomTheme) => ({
  title: {
    marginBottom: '22px!important',
  },
  buttonContainer: {
    paddingTop: 40,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    [theme.breakpoints.down('xs')]: {
      borderTop: 'none',
      paddingTop: 0,
    },
  },
}));

const accountInfoTitle = {
  en: 'Requesting Member',
  it: 'Membro richiedente',
};

const SummaryStep = () => {
  const PATHS = buildPATHS();
  const { summarySteps } = useBuildSummaryForm();

  const {
    textsOverwritings,
    registerData,
    language,
    previewForm,
    formInitialValues,
    customFields,
    summaryPageFieldsFromAccount,
    summaryText,
    magicFieldsData,
  } = useSelector((state: RootStore) => ({
    // globalConfig state
    textsOverwritings: textsOverwritingsSelector(state),
    // formConfig state
    registerData: registerDataSelector(state),
    // app state
    language: languageSelector(state),
    previewForm: previewFormSelector(state),
    formInitialValues: formInitialValuesSelector(state),
    customFields: customFieldsSelector(state),
    summaryPageFieldsFromAccount: summaryPageFieldsFromAccountSelector(state),
    summaryText: summaryTextSelector(state),
    magicFieldsData: magicFieldsDataSelector(state),
  }));

  const { t } = useTranslation();
  const classes = useTitleStyles();

  const history = useHistory();
  const dispatch = useDispatch();

  const isMagicFieldsData =
    magicFieldsData && Object.keys(magicFieldsData).length > 0;

  const handleSendData = () => {
    const sendData = {
      ...formInitialValues,
      ...registerData.reduce((acc: Dict<any>, current: Dict<any>) => {
        return { ...acc, ...current };
      }, {}),
    };
    if (previewForm) {
      dispatch(allActions.formConfigActions.submitPreviewForm());
      history.push(PATHS_WITH_PARAMS.application_sent);
      return;
    }

    dispatch(
      allActions.formConfigActions.submitForm(
        {
          fields: mapDataBeforeSave(sendData, customFields),
          language,
        },
        history,
        getPathWithBaseUrl(PATHS_WITH_PARAMS.application_sent),
      ),
    );
  };

  const renderSummaryText = () => {
    return summaryText ? (
      <div className="hid-layout__mb-05">
        <Typography variant={TypographyVariantEnum.BodyLongMarketing}>
          {isMagicFieldsData
            ? replaceMagicFields(summaryText[language], magicFieldsData)
            : summaryText[language]}
        </Typography>
      </div>
    ) : null;
  };

  const renderUserProfileSummaryPageFields = () => {
    if (
      isAuthenticated() &&
      summaryPageFieldsFromAccount &&
      summaryPageFieldsFromAccount.length > 0
    ) {
      return (
        <div className="hid-spacing__mb-09">
          <FormText
            // eslint-disable-next-line react/no-array-index-key
            key={`title-${1}`}
            className="user-info-title"
            text={accountInfoTitle[language]}
            type={TEXT_TYPES.SECTION_HEADER}
          />
          <AccountDetails fieldsToDisplay={summaryPageFieldsFromAccount} />
        </div>
      );
    }
    return null;
  };
  return (
    <>
      <FormText
        className={classes.title}
        type={TEXT_TYPES.SECTION_TEXT}
        text="REGISTER_SUMMARY"
      />
      {renderSummaryText()}
      {renderUserProfileSummaryPageFields()}
      {summarySteps}
      <Box
        className={classes.buttonContainer}
        display="flex"
        justifyContent="flex-end"
      >
        <ButtonNavigation onClick={handleSendData} next>
          {getTranslation(textsOverwritings ?? {}, 'BUTTON_SEND_FORM') ||
            t('BUTTON_SEND_FORM')}
        </ButtonNavigation>
      </Box>
    </>
  );
};

export default SummaryStep;
